import React, { lazy } from "react";
import { matchPath } from "react-router-dom";
import type { Params } from "react-router-dom";

export interface GeneratedPageProps {
  data?: any;
  params?: Params<string>;
}

export interface GeneratedPageConfig {
  pageId: string;
  routePath: string;
  dataKeys: string[];
  Component: React.LazyExoticComponent<React.ComponentType<GeneratedPageProps>>;
}

export const pageConfigs: GeneratedPageConfig[] = [
  {
    pageId: "home",
    routePath: "/",
    dataKeys: [],
    Component: lazy(() => import("./home/index")),
  },
  {
    pageId: "role-list",
    routePath: "/role",
    dataKeys: ["role_list", "factions"],
    Component: lazy(() => import("./role-list/index")),
  },
  {
    pageId: "role-detail",
    routePath: "/role/:roleId",
    dataKeys: ["role_list", "factions", "map_list", "map_roles"],
    Component: lazy(() => import("./role-detail/index")),
  },
  {
    pageId: "armament-list",
    routePath: "/armament",
    dataKeys: [
      "armament_equipment_models", "armament_equipment_types", "armament_equipment_data",
      "armament_weapon_models", "armament_weapon_types", "armament_weapon_data",
      "armament_goods", "armament_skill_models", "armament_skill_types",
      "armament_skill_data", "armament_secret", "armament_items", "armament_legendary",
      "camp_list",
    ],
    Component: lazy(() => import("./armament-list/index")),
  },
  {
    pageId: "camp-list",
    routePath: "/camp",
    dataKeys: ["camp_list"],
    Component: lazy(() => import("./camp-list/index")),
  },
  {
    pageId: "camp-detail",
    routePath: "/camp/:gangId",
    dataKeys: ["camp_list", "role_list", "factions", "camp_record_list"],
    Component: lazy(() => import("./camp-detail/index")),
  },
  {
    pageId: "event-list",
    routePath: "/event",
    dataKeys: ["event_list"],
    Component: lazy(() => import("./event-list/index")),
  },
  {
    pageId: "event-detail",
    routePath: "/event/:eventId",
    dataKeys: ["event_list"],
    Component: lazy(() => import("./event-detail/index")),
  },
  {
    pageId: "map-list",
    routePath: "/map",
    dataKeys: ["map_list"],
    Component: lazy(() => import("./map-list/index")),
  },
  {
    pageId: "map-detail",
    routePath: "/map/:mapId",
    dataKeys: ["map_list", "map_roles"],
    Component: lazy(() => import("./map-detail/index")),
  },
  {
    pageId: "anecdote-list",
    routePath: "/anecdote",
    dataKeys: ["anecdote_list"],
    Component: lazy(() => import("./anecdote-list/index")),
  },
  {
    pageId: "record-list",
    routePath: "/record",
    dataKeys: ["record_list"],
    Component: lazy(() => import("./record-list/index")),
  },
  {
    pageId: "explore-list",
    routePath: "/explore",
    dataKeys: ["explore_list"],
    Component: lazy(() => import("./explore-list/index")),
  },
];

export const normalizeRoutePath = (routePath: string) => {
  if (!routePath || routePath === "/") {
    return "/";
  }
  const normalized = routePath.replace(/\/+/g, "/").replace(/\/$/, "");
  return normalized.startsWith("/") ? normalized : `/${normalized}`;
};

export const resolvePage = (pathname: string) => {
  const normalizedPath = normalizeRoutePath(pathname);
  for (const config of pageConfigs) {
    const m = matchPath(
      { path: normalizeRoutePath(config.routePath), end: true },
      normalizedPath,
    );
    if (m) return { config, params: m.params };
  }
  return null;
};
